import { Component } from "react";
import { Button } from "@/components/ui/button";
import { AlertTriangle, RotateCcw } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Attrape les erreurs de rendu React et affiche un fallback lisible
 * au lieu de laisser la page vide (fond seul). Envoie aussi un email
 * automatique à Antoine (via /api/errors/report) pour tracer les bugs
 * en temps réel sans dépendre des retours utilisateurs.
 *
 * Le "resetKey" (typiquement pathname) réinitialise l'état d'erreur
 * quand l'utilisateur change de route — évite d'être bloqué sur
 * l'écran d'erreur en navigant ailleurs.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary] uncaught render error:", error, info);
    // Fire-and-forget : signaler à l'admin sans jamais bloquer l'UI.
    // Dédupliqué sur 30s pour éviter de spammer l'inbox si React re-render en boucle.
    try {
      const msg = error?.message || String(error || "");
      const key = `${window.location.pathname}::${msg}`;
      const last = window.__lastErrReport || {};
      const now = Date.now();
      if (last.key === key && now - (last.ts || 0) < 30000) return;
      window.__lastErrReport = { key, ts: now };
      api.post("/errors/report", {
        message: msg,
        stack: error?.stack || "",
        component_stack: info?.componentStack || "",
        page: window.location.pathname + window.location.search,
        user_agent: navigator.userAgent || "",
      }).catch(() => { /* silencieux : ne jamais casser l'UI */ });
    } catch { /* ignore */ }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.resetKey !== this.props.resetKey && this.state.hasError) {
      this.setState({ hasError: false, error: null });
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    const msg = this.state.error?.message || String(this.state.error || "");
    return (
      <div className="max-w-xl mx-auto mt-16 text-center space-y-4" data-testid="error-boundary">
        <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-destructive/15 text-destructive border border-destructive/40">
          <AlertTriangle className="h-7 w-7" />
        </div>
        <h2 className="font-brand text-2xl">Oups… cette page n'a pas pu s'afficher</h2>
        <p className="text-sm text-muted-foreground">
          Un souci technique est survenu au chargement. Antoine a été notifié automatiquement par email —
          actualisez la page, si le problème persiste il reviendra vers vous rapidement.
        </p>
        {msg && (
          <pre className="text-[11px] text-left text-muted-foreground bg-muted border border-border/60 rounded-lg p-3 whitespace-pre-wrap break-words max-h-40 overflow-auto">
            {msg}
          </pre>
        )}
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            className="rounded-full"
            onClick={() => this.setState({ hasError: false, error: null })}
            data-testid="error-retry-btn"
          >
            <RotateCcw className="h-4 w-4 mr-2" /> Réessayer
          </Button>
          <Button
            className="rounded-full bg-primary"
            onClick={() => window.location.reload()}
            data-testid="error-reload-btn"
          >
            Actualiser la page
          </Button>
        </div>
      </div>
    );
  }
}
